/**
 * UI text in the language the Trimble Connect user has chosen in their account settings
 * (`user.getSettings().language`, kept live via the `user.onSettingsChanged` event — see
 * `tc/workspace.ts`). Falls back to English for a language this viewer doesn't carry a
 * translation for, and for the standalone case where no host is around to ask.
 *
 * A handful of technical acronyms (CRS, GSD, DOM, ID) are kept in their English/international
 * form in every language — that's how they're used in this domain regardless of UI language.
 */
export type Lang = "en" | "de" | "fr" | "it";

export interface Strings {
  // ------------------------------------------------------------------------------- chrome
  documentTitle: string;
  appTitle: string;
  collapsePanel: string;
  showPanel: string;
  starting: string;
  dropHint: string;

  // ---------------------------------------------------------------------- 2D / 3D toggle
  view2D: string;
  view3D: string;
  view2DTitle: string;
  view3DTitle: string;

  // ------------------------------------------------------------------------------ status
  statusConnecting: string;
  statusReady: string;
  statusConnectedTo(name: string): string;
  statusNoProject: string;
  statusStandalone: string;
  statusFilesUnavailable: string;

  // --------------------------------------------------------------------------- auth gate
  authGateTitle: string;
  authGateSigningIn: string;
  authGateSignInFailed(error: string): string;
  authGateNotWhitelisted(email: string): string;
  authGateError(error: string): string;
  authGateRetry: string;
  standaloneBlockedTitle: string;
  standaloneBlockedMessage: string;
  statusResolvingProject(name: string): string;
  statusResolvingProjectGeneric: string;
  statusOpening(label: string): string;
  statusReading(label: string): string;
  statusIndexing(label: string): string;
  statusSaving(name: string): string;
  statusUpdatingMeasurements: string;
  statusOpeningShareLink: string;
  statusLocatingTiles(done: number, total: number): string;
  statusReadingElevations(done: number, total: number): string;

  // ------------------------------------------------------------------------------ toasts
  toastActionFailed(label: string, error: string): string;
  toastCouldNotReachFiles(error: string): string;
  toastGeographicCrs(label: string): string;
  toastFolderPairAmbiguous(name: string): string;
  toastCrsMismatch(name: string, mismatch: string): string;
  toastNoOffset(label: string): string;
  toastTokenAndProjectRequired: string;
  toastReachedProject(name: string, baseUrl: string): string;
  toastNotShareLink: string;
  toastShareOpened(count: number, permission: string, baseUrl: string): string;
  toastShareFolder: string;
  toastUnsupportedFile(name: string): string;
  toastCouldNotLoadMeasurements(error: string): string;
  toastConnectToSaveMeasurements: string;
  toastOpenModelFirstToSave: string;
  toastSavedWith(name: string, model: string): string;
  toastOpenDatasetFirst: string;

  // -------------------------------------------------------------------------- data section
  data: string;
  loaded: string;
  loadedFolder(name: string): string;
  remove: string;
  filesUnavailableHint: string;
  standaloneHint: string;
  useAccessToken: string;
  loadingBadge: string;

  // ----------------------------------------------------------------------- terrain section
  terrain: string;
  tilesBadge(done: number, total: number): string;
  visible: string;
  opacity: string;
  wireframe: string;
  backdropOpacity: string;
  imageryGsd: string;
  modelGsd: string;
  tileSize: string;
  textures: string;

  // -------------------------------------------------------------------------- mesh section
  mesh: string;
  shading: string;
  shadingUnlit: string;
  shadingLit: string;
  offset: string;
  offsetSource: string;
  triangles: string;
  resident: string;
  located: string;
  estimatedExtents: string;
  tilePlacement: string;
  tilePlacementExact: string;
  tilePlacementFast: string;
  tilePlacementHint: string;
  setOffsetManually: string;
  offsetPrompt: string;

  // -------------------------------------------------------------------------- view section
  view: string;
  verticalExaggeration: string;
  sunAzimuth: string;
  sunElevation: string;
  brightness: string;
  zoomToExtent: string;
  topDown: string;
  toggleTerrain: string;

  // -------------------------------------------------------------------------- info section
  info: string;
  project: string;
  projectCrs: string;
  regionApi: string;
  host: string;
  hostStandalone: string;
  shareLinks: string;
  shareRegion: string;
  orthoCrs: string;
  orthoSize: string;
  orthoLevels: string;
  domCrs: string;
  domSize: string;
  backdrop: string;
  backdropSwisstopo: string;
  backdropNone: string;
  sceneOrigin: string;
  extent: string;
  elevation: string;
  infoHint: string;
  eyeDistance: string;

  // -------------------------------------------------------------------------------- modal
  tokenModalTitle: string;
  tokenModalHint: string;
  accessToken: string;
  projectId: string;
  connect: string;

  // ------------------------------------------------------------------------- project tree
  loadingFolder: string;
  folderListError(error: string): string;
  folderEmpty: string;
  loadFolderPair(name: string, count: number): string;

  // ------------------------------------------------------------------------ measure toolbox
  measureLine: string;
  measureArea: string;
  measureVolume: string;
  savedFor(model: string): string;
  saved: string;
  noSavedForModel: string;
  openModelToSave: string;
  showMeasurement: string;
  renameMeasurement: string;
  deleteMeasurement: string;
  deleteConfirm(name: string): string;
  undoPoint: string;
  finish: string;
  editPoints: string;
  saveMeasurement: string;
  clearShape: string;
  lengthLabel: string;
  perimeterLabel: string;
  lengthSoFarLabel: string;
  areaPlan: string;
  volumeEst: string;
  baseElevation: string;
  hintClosed: string;
  hintLine: string;
  hintArea: string;
  namePrompt: string;
  defaultLineName: string;
  defaultAreaName: string;
  defaultVolumeName: string;

  // ------------------------------------------------------------------------ TC navigation
  menuTitle: string;

  // --------------------------------------------------------------------------- plan mode
  modeViewer: string;
  modePlan: string;
  modeViewerTitle: string;
  modePlanTitle: string;
  orthophotos: string;
  noOrthophotosLoaded: string;
  addTiffButton: string;
  addOrthophotoHint: string;
  overlapBadge: string;
  bringToFront: string;
  layerVisible: string;
  layerHidden: string;
  removeLayer: string;
  toastPlanCrsMismatch(name: string, mismatch: string): string;
  printExport: string;
  scaleLabel: string;
  paperSize: string;
  orientationLabel: string;
  orientationPortrait: string;
  orientationLandscape: string;
  dpiLabel: string;
  exportPdf: string;
  statusExportingPdf: string;
  toastExportedPdf: string;
  toastExportFailed(error: string): string;
  exportNeedsLayer: string;
  annotate: string;
  toolPan: string;
  toolPen: string;
  toolText: string;
  penColor: string;
  penWidth: string;
  undoAnnotation: string;
  clearAnnotations: string;
  confirmClearAnnotations: string;
  textPrompt: string;
}

const en: Strings = {
  documentTitle: "Geo 3D Viewer — Trimble Connect",
  appTitle: "Geo 3D Viewer",
  collapsePanel: "Collapse panel",
  showPanel: "Show panel",
  starting: "Starting…",
  dropHint: "Drop GeoTIFF / mesh zip / OBJ set here",

  view2D: "2D",
  view3D: "3D",
  view2DTitle: "Switch to 2D plan view (locked top-down, pan & zoom only)",
  view3DTitle: "Switch to 3D view (free orbit)",

  statusConnecting: "Connecting to Trimble Connect…",
  statusReady: "Ready",
  statusConnectedTo: (name) => `Connected to “${name}”`,
  statusNoProject: "Connected, but no project is open",
  statusStandalone: "Running standalone — open local files or a URL",
  statusFilesUnavailable: "Connected, but project files are unavailable",

  authGateTitle: "Geo 3D Viewer",
  authGateSigningIn: "Checking your Trimble Connect account…",
  authGateSignInFailed: (error) => `Could not sign in through Trimble Connect: ${error}`,
  authGateNotWhitelisted: (email) =>
    `Your Trimble account (${email}) isn't whitelisted for this tool yet. Ask an admin to add it.`,
  authGateError: (error) => `Could not reach the server: ${error}`,
  authGateRetry: "Try again",
  standaloneBlockedTitle: "Geo 3D Viewer",
  standaloneBlockedMessage: "This viewer only works as a Trimble Connect extension. Open it from within a Trimble Connect project.",
  statusResolvingProject: (name) => `Resolving project ${name}…`,
  statusResolvingProjectGeneric: "Resolving project",
  statusOpening: (label) => `Opening ${label}`,
  statusReading: (label) => `Reading ${label}`,
  statusIndexing: (label) => `Indexing ${label}`,
  statusSaving: (name) => `Saving “${name}”`,
  statusUpdatingMeasurements: "Updating saved measurements",
  statusOpeningShareLink: "Opening share link",
  statusLocatingTiles: (done, total) => `Locating mesh tiles ${done}/${total}`,
  statusReadingElevations: (done, total) => `Reading elevations ${done}/${total}`,

  toastActionFailed: (label, error) => `${label} failed: ${error}`,
  toastCouldNotReachFiles: (error) => `Could not reach the project files: ${error}`,
  toastGeographicCrs: (label) =>
    `${label} is in geographic (lon/lat) coordinates. This viewer works in a projected CRS ` +
    `and does not reproject, so it will not line up with the mesh.`,
  toastFolderPairAmbiguous: (name) =>
    `Could not tell a DOM (height) and an Ortho (photo) GeoTIFF apart in “${name}”. Open the ` +
    `files individually instead.`,
  toastCrsMismatch: (name, mismatch) => `${name}: ${mismatch}`,
  toastNoOffset: (label) =>
    `${label} has no georeferencing offset — placed at the scene origin. Use “Set offset ` +
    `manually…” in the Mesh panel below to position it correctly.`,
  toastTokenAndProjectRequired: "Both an access token and a project ID are required.",
  toastReachedProject: (name, baseUrl) => `Reached ${name} via ${baseUrl}`,
  toastNotShareLink: "That does not look like a Trimble Connect share link.",
  toastShareOpened: (count, permission, baseUrl) =>
    `Share opened: ${count} object(s), permission ${permission}, via ${baseUrl}`,
  toastShareFolder: "The share contains a folder — browse it below to pick files.",
  toastUnsupportedFile: (name) => `${name}: unsupported file type for this viewer.`,
  toastCouldNotLoadMeasurements: (error) => `Could not load saved measurements: ${error}`,
  toastConnectToSaveMeasurements: "Connect to a Trimble Connect project to save measurements.",
  toastOpenModelFirstToSave: "Open an orthophoto or mesh first — measurements are saved alongside the model.",
  toastSavedWith: (name, model) => `Saved “${name}” with ${model}.`,
  toastOpenDatasetFirst: "Open a dataset first so the coordinate system is set.",

  data: "Data",
  loaded: "Loaded",
  loadedFolder: (name) => `Loaded — 📦 ${name}`,
  remove: "Remove",
  filesUnavailableHint: "Project files are unavailable — check that the extension was granted access.",
  standaloneHint: "Not running inside Trimble Connect. Drag and drop a file onto the viewer, or use an access token below.",
  useAccessToken: "Use an access token…",
  loadingBadge: "loading",

  terrain: "Terrain",
  tilesBadge: (done, total) => `${done}/${total} tiles`,
  visible: "Visible",
  opacity: "Opacity",
  wireframe: "Wireframe",
  backdropOpacity: "Backdrop opacity",
  imageryGsd: "Imagery GSD",
  modelGsd: "Model GSD",
  tileSize: "Tile size",
  textures: "Textures",

  mesh: "Mesh",
  shading: "Shading",
  shadingUnlit: "Unlit (as captured)",
  shadingLit: "Lit (relief shading)",
  offset: "Offset",
  offsetSource: "Offset source",
  triangles: "Triangles",
  resident: "Resident",
  located: "Located",
  estimatedExtents: "Estimated extents",
  tilePlacement: "Tile placement",
  tilePlacementExact: "Exact (reads ~12% of archive)",
  tilePlacementFast: "Fast estimate (~1%)",
  tilePlacementHint:
    "Tile extents decide which tiles load first. Exact scans each tile's vertex block; the " +
    "fast estimate samples it and can be off by a quarter of a tile.",
  setOffsetManually: "Set offset manually…",
  offsetPrompt: "Mesh offset as “east north height” in the project CRS:",

  view: "View",
  verticalExaggeration: "Vertical exaggeration",
  sunAzimuth: "Sun azimuth",
  sunElevation: "Sun elevation",
  brightness: "Brightness",
  zoomToExtent: "Zoom to extent",
  topDown: "Top-down",
  toggleTerrain: "Toggle terrain",

  info: "Info",
  project: "Project",
  projectCrs: "Project CRS",
  regionApi: "Region API",
  host: "Host",
  hostStandalone: "standalone",
  shareLinks: "Share links",
  shareRegion: "Share region",
  orthoCrs: "Ortho CRS",
  orthoSize: "Ortho size",
  orthoLevels: "Ortho levels",
  domCrs: "DOM CRS",
  domSize: "DOM size",
  backdrop: "Backdrop",
  backdropSwisstopo: "swisstopo SWISSIMAGE",
  backdropNone: "none",
  sceneOrigin: "Scene origin",
  extent: "Extent",
  elevation: "Elevation",
  infoHint:
    "Layers are assumed to share one projected CRS; no reprojection is performed. Coordinates " +
    "under the cursor are shown bottom-right.",
  eyeDistance: "eye",

  tokenModalTitle: "Use a Trimble Connect access token",
  tokenModalHint:
    "Only needed outside Trimble Connect. The token is the signed-in user's Trimble Identity " +
    "token; it is held in memory for this session and never stored.",
  accessToken: "Access token",
  projectId: "Project ID",
  connect: "Connect",

  loadingFolder: "Loading…",
  folderListError: (error) => `Could not list the folder: ${error}`,
  folderEmpty: "This folder is empty.",
  loadFolderPair: (name, count) => `📦 Load DOM + Ortho from “${name}” (${count} rasters)`,

  measureLine: "Measure a line",
  measureArea: "Measure an area (plan)",
  measureVolume: "Estimate a volume",
  savedFor: (model) => `Saved · ${model}`,
  saved: "Saved",
  noSavedForModel: "No measurements saved for this model yet.",
  openModelToSave: "Open an orthophoto or mesh to save measurements against it.",
  showMeasurement: "Show this measurement",
  renameMeasurement: "Rename",
  deleteMeasurement: "Delete",
  deleteConfirm: (name) => `Delete “${name}”?`,
  undoPoint: "Undo last point",
  finish: "Finish",
  editPoints: "Edit points",
  saveMeasurement: "Save this measurement",
  clearShape: "Clear this shape",
  lengthLabel: "Length",
  perimeterLabel: "Perimeter",
  lengthSoFarLabel: "Length so far",
  areaPlan: "Area (plan)",
  volumeEst: "Volume (est.)",
  baseElevation: "Base elevation (m)",
  hintClosed: "Drag a point to move it, Clear to remeasure, save to keep it, or pick another tool to switch.",
  hintLine: "Click points on the model; Finish to lock the line.",
  hintArea: "Click 3+ points on the model; Finish to close the shape.",
  namePrompt: "Name for this measurement:",
  defaultLineName: "Line",
  defaultAreaName: "Area",
  defaultVolumeName: "Volume",

  menuTitle: "Geo 3D Viewer",

  modeViewer: "3D Viewer",
  modePlan: "Orthophoto Plan",
  modeViewerTitle: "Switch to the 3D viewer",
  modePlanTitle: "Switch to orthophoto plan export",
  orthophotos: "Orthophotos",
  noOrthophotosLoaded: "No TIFFs loaded yet — open one from the project browser or drop it here.",
  addTiffButton: "Add TIFF file(s)…",
  addOrthophotoHint: "Open a TIFF from the project browser, or drop one onto the canvas. Add as many as you like.",
  overlapBadge: "overlaps",
  bringToFront: "Bring to front",
  layerVisible: "Visible — click to hide",
  layerHidden: "Hidden — click to show",
  removeLayer: "Remove",
  toastPlanCrsMismatch: (name, mismatch) => `${name}: ${mismatch} — positions may not line up.`,
  printExport: "Print & export",
  scaleLabel: "Scale",
  paperSize: "Paper",
  orientationLabel: "Orientation",
  orientationPortrait: "Portrait",
  orientationLandscape: "Landscape",
  dpiLabel: "Resolution",
  exportPdf: "Export PDF",
  statusExportingPdf: "Rendering PDF…",
  toastExportedPdf: "PDF exported.",
  toastExportFailed: (error) => `Export failed: ${error}`,
  exportNeedsLayer: "Load at least one orthophoto first.",
  annotate: "Annotate",
  toolPan: "Pan",
  toolPen: "Paint",
  toolText: "Text",
  penColor: "Colour",
  penWidth: "Pen width",
  undoAnnotation: "Undo",
  clearAnnotations: "Clear all",
  confirmClearAnnotations: "Clear every annotation?",
  textPrompt: "Text to add:",
};

const de: Strings = {
  documentTitle: "Geo-3D-Viewer — Trimble Connect",
  appTitle: "Geo-3D-Viewer",
  collapsePanel: "Panel einklappen",
  showPanel: "Panel anzeigen",
  starting: "Wird gestartet…",
  dropHint: "GeoTIFF / Mesh-ZIP / OBJ-Satz hier ablegen",

  view2D: "2D",
  view3D: "3D",
  view2DTitle: "Zur 2D-Planansicht wechseln (fest von oben, nur schwenken & zoomen)",
  view3DTitle: "Zur 3D-Ansicht wechseln (frei drehbar)",

  statusConnecting: "Verbindung zu Trimble Connect wird hergestellt…",
  statusReady: "Bereit",
  statusConnectedTo: (name) => `Verbunden mit „${name}“`,
  statusNoProject: "Verbunden, aber es ist kein Projekt geöffnet",
  statusStandalone: "Eigenständig ausgeführt — lokale Dateien oder eine URL öffnen",
  statusFilesUnavailable: "Verbunden, aber die Projektdateien sind nicht verfügbar",

  authGateTitle: "Geo 3D Viewer",
  authGateSigningIn: "Trimble-Connect-Konto wird geprüft…",
  authGateSignInFailed: (error) => `Anmeldung über Trimble Connect fehlgeschlagen: ${error}`,
  authGateNotWhitelisted: (email) =>
    `Ihr Trimble-Konto (${email}) ist für dieses Tool noch nicht freigeschaltet. Bitten Sie einen Administrator, es hinzuzufügen.`,
  authGateError: (error) => `Server nicht erreichbar: ${error}`,
  authGateRetry: "Erneut versuchen",
  standaloneBlockedTitle: "Geo 3D Viewer",
  standaloneBlockedMessage: "Dieser Viewer funktioniert nur als Trimble-Connect-Erweiterung. Bitte innerhalb eines Trimble-Connect-Projekts öffnen.",
  statusResolvingProject: (name) => `Projekt ${name} wird aufgelöst…`,
  statusResolvingProjectGeneric: "Projekt wird aufgelöst",
  statusOpening: (label) => `${label} wird geöffnet`,
  statusReading: (label) => `${label} wird gelesen`,
  statusIndexing: (label) => `${label} wird indiziert`,
  statusSaving: (name) => `„${name}“ wird gespeichert`,
  statusUpdatingMeasurements: "Gespeicherte Messungen werden aktualisiert",
  statusOpeningShareLink: "Freigabelink wird geöffnet",
  statusLocatingTiles: (done, total) => `Mesh-Kacheln werden lokalisiert ${done}/${total}`,
  statusReadingElevations: (done, total) => `Höhenwerte werden gelesen ${done}/${total}`,

  toastActionFailed: (label, error) => `${label} fehlgeschlagen: ${error}`,
  toastCouldNotReachFiles: (error) => `Projektdateien nicht erreichbar: ${error}`,
  toastGeographicCrs: (label) =>
    `${label} liegt in geografischen (Länge/Breite) Koordinaten vor. Dieser Viewer arbeitet ` +
    `in einem projizierten Koordinatensystem und projiziert nicht um, daher passt es nicht ` +
    `zum Mesh.`,
  toastFolderPairAmbiguous: (name) =>
    `In „${name}“ konnten ein DOM (Höhenmodell) und ein Ortho (Foto) GeoTIFF nicht ` +
    `unterschieden werden. Öffnen Sie die Dateien stattdessen einzeln.`,
  toastCrsMismatch: (name, mismatch) => `${name}: ${mismatch}`,
  toastNoOffset: (label) =>
    `${label} hat keinen Georeferenzierungs-Offset — wurde am Szenenursprung platziert. ` +
    `Verwenden Sie „Offset manuell setzen…“ im Mesh-Panel, um es korrekt zu positionieren.`,
  toastTokenAndProjectRequired: "Zugriffstoken und Projekt-ID werden beide benötigt.",
  toastReachedProject: (name, baseUrl) => `${name} erreicht über ${baseUrl}`,
  toastNotShareLink: "Das sieht nicht wie ein Trimble-Connect-Freigabelink aus.",
  toastShareOpened: (count, permission, baseUrl) =>
    `Freigabe geöffnet: ${count} Objekt(e), Berechtigung ${permission}, über ${baseUrl}`,
  toastShareFolder: "Die Freigabe enthält einen Ordner — unten durchsuchen, um Dateien auszuwählen.",
  toastUnsupportedFile: (name) => `${name}: nicht unterstützter Dateityp für diesen Viewer.`,
  toastCouldNotLoadMeasurements: (error) => `Gespeicherte Messungen konnten nicht geladen werden: ${error}`,
  toastConnectToSaveMeasurements: "Mit einem Trimble-Connect-Projekt verbinden, um Messungen zu speichern.",
  toastOpenModelFirstToSave: "Öffnen Sie zuerst ein Orthofoto oder Mesh — Messungen werden zusammen mit dem Modell gespeichert.",
  toastSavedWith: (name, model) => `„${name}“ mit ${model} gespeichert.`,
  toastOpenDatasetFirst: "Öffnen Sie zuerst einen Datensatz, damit das Koordinatensystem feststeht.",

  data: "Daten",
  loaded: "Geladen",
  loadedFolder: (name) => `Geladen — 📦 ${name}`,
  remove: "Entfernen",
  filesUnavailableHint: "Projektdateien sind nicht verfügbar — prüfen Sie, ob der Erweiterung Zugriff gewährt wurde.",
  standaloneHint: "Läuft nicht innerhalb von Trimble Connect. Datei per Drag & Drop auf den Viewer ziehen oder unten ein Zugriffstoken verwenden.",
  useAccessToken: "Zugriffstoken verwenden…",
  loadingBadge: "lädt",

  terrain: "Gelände",
  tilesBadge: (done, total) => `${done}/${total} Kacheln`,
  visible: "Sichtbar",
  opacity: "Deckkraft",
  wireframe: "Drahtgitter",
  backdropOpacity: "Hintergrund-Deckkraft",
  imageryGsd: "Bild-GSD",
  modelGsd: "Modell-GSD",
  tileSize: "Kachelgröße",
  textures: "Texturen",

  mesh: "Mesh",
  shading: "Schattierung",
  shadingUnlit: "Unbeleuchtet (wie aufgenommen)",
  shadingLit: "Beleuchtet (Reliefschattierung)",
  offset: "Offset",
  offsetSource: "Offset-Quelle",
  triangles: "Dreiecke",
  resident: "Im Speicher",
  located: "Lokalisiert",
  estimatedExtents: "Geschätzte Ausdehnungen",
  tilePlacement: "Kachelplatzierung",
  tilePlacementExact: "Exakt (liest ~12 % des Archivs)",
  tilePlacementFast: "Schnelle Schätzung (~1 %)",
  tilePlacementHint:
    "Kachel-Ausdehnungen bestimmen, welche Kacheln zuerst laden. „Exakt“ liest den " +
    "Vertex-Block jeder Kachel; die schnelle Schätzung tastet ihn ab und kann um bis zu " +
    "eine Viertelkachel danebenliegen.",
  setOffsetManually: "Offset manuell setzen…",
  offsetPrompt: "Mesh-Offset als „Ost Nord Höhe“ im Projekt-Koordinatensystem:",

  view: "Ansicht",
  verticalExaggeration: "Höhenüberhöhung",
  sunAzimuth: "Sonnen-Azimut",
  sunElevation: "Sonnenhöhe",
  brightness: "Helligkeit",
  zoomToExtent: "Auf Ausdehnung zoomen",
  topDown: "Draufsicht",
  toggleTerrain: "Gelände ein/aus",

  info: "Info",
  project: "Projekt",
  projectCrs: "Projekt-Koordinatensystem",
  regionApi: "Regions-API",
  host: "Host",
  hostStandalone: "eigenständig",
  shareLinks: "Freigabelinks",
  shareRegion: "Freigabe-Region",
  orthoCrs: "Ortho-Koordinatensystem",
  orthoSize: "Ortho-Größe",
  orthoLevels: "Ortho-Stufen",
  domCrs: "DOM-Koordinatensystem",
  domSize: "DOM-Größe",
  backdrop: "Hintergrund",
  backdropSwisstopo: "swisstopo SWISSIMAGE",
  backdropNone: "keiner",
  sceneOrigin: "Szenenursprung",
  extent: "Ausdehnung",
  elevation: "Höhe",
  infoHint:
    "Es wird angenommen, dass alle Ebenen ein projiziertes Koordinatensystem teilen; es " +
    "findet keine Umprojektion statt. Koordinaten unter dem Cursor werden unten rechts angezeigt.",
  eyeDistance: "Blick",

  tokenModalTitle: "Trimble-Connect-Zugriffstoken verwenden",
  tokenModalHint:
    "Nur außerhalb von Trimble Connect nötig. Das Token ist das Trimble-Identity-Token des " +
    "angemeldeten Benutzers; es wird nur im Arbeitsspeicher dieser Sitzung gehalten und nie gespeichert.",
  accessToken: "Zugriffstoken",
  projectId: "Projekt-ID",
  connect: "Verbinden",

  loadingFolder: "Wird geladen…",
  folderListError: (error) => `Ordner konnte nicht aufgelistet werden: ${error}`,
  folderEmpty: "Dieser Ordner ist leer.",
  loadFolderPair: (name, count) => `📦 DOM + Ortho aus „${name}“ laden (${count} Raster)`,

  measureLine: "Strecke messen",
  measureArea: "Fläche messen (Grundriss)",
  measureVolume: "Volumen schätzen",
  savedFor: (model) => `Gespeichert · ${model}`,
  saved: "Gespeichert",
  noSavedForModel: "Für dieses Modell sind noch keine Messungen gespeichert.",
  openModelToSave: "Öffnen Sie ein Orthofoto oder Mesh, um Messungen dazu zu speichern.",
  showMeasurement: "Diese Messung anzeigen",
  renameMeasurement: "Umbenennen",
  deleteMeasurement: "Löschen",
  deleteConfirm: (name) => `„${name}“ löschen?`,
  undoPoint: "Letzten Punkt rückgängig machen",
  finish: "Abschließen",
  editPoints: "Punkte bearbeiten",
  saveMeasurement: "Diese Messung speichern",
  clearShape: "Diese Form löschen",
  lengthLabel: "Länge",
  perimeterLabel: "Umfang",
  lengthSoFarLabel: "Länge bisher",
  areaPlan: "Fläche (Grundriss)",
  volumeEst: "Volumen (geschätzt)",
  baseElevation: "Basishöhe (m)",
  hintClosed: "Punkt ziehen, um ihn zu verschieben, löschen, um neu zu messen, speichern, um sie zu behalten, oder ein anderes Werkzeug wählen, um zu wechseln.",
  hintLine: "Punkte auf dem Modell anklicken; Abschließen sperrt die Linie.",
  hintArea: "3 oder mehr Punkte auf dem Modell anklicken; Abschließen schließt die Form.",
  namePrompt: "Name für diese Messung:",
  defaultLineName: "Strecke",
  defaultAreaName: "Fläche",
  defaultVolumeName: "Volumen",

  menuTitle: "Geo-3D-Viewer",

  modeViewer: "3D-Viewer",
  modePlan: "Orthofoto-Plan",
  modeViewerTitle: "Zum 3D-Viewer wechseln",
  modePlanTitle: "Zum Orthofoto-Plan-Export wechseln",
  orthophotos: "Orthofotos",
  noOrthophotosLoaded: "Noch keine TIFFs geladen — im Projektbrowser öffnen oder hier ablegen.",
  addTiffButton: "TIFF-Datei(en) hinzufügen…",
  addOrthophotoHint: "TIFF im Projektbrowser öffnen oder auf die Fläche ziehen. Beliebig viele möglich.",
  overlapBadge: "überlappt",
  bringToFront: "In den Vordergrund",
  layerVisible: "Sichtbar — klicken zum Ausblenden",
  layerHidden: "Ausgeblendet — klicken zum Einblenden",
  removeLayer: "Entfernen",
  toastPlanCrsMismatch: (name, mismatch) => `${name}: ${mismatch} — Lage passt evtl. nicht.`,
  printExport: "Druck & Export",
  scaleLabel: "Maßstab",
  paperSize: "Papierformat",
  orientationLabel: "Ausrichtung",
  orientationPortrait: "Hochformat",
  orientationLandscape: "Querformat",
  dpiLabel: "Auflösung",
  exportPdf: "PDF exportieren",
  statusExportingPdf: "PDF wird erstellt…",
  toastExportedPdf: "PDF exportiert.",
  toastExportFailed: (error) => `Export fehlgeschlagen: ${error}`,
  exportNeedsLayer: "Zuerst mindestens ein Orthofoto laden.",
  annotate: "Anmerkungen",
  toolPan: "Verschieben",
  toolPen: "Malen",
  toolText: "Text",
  penColor: "Farbe",
  penWidth: "Stiftbreite",
  undoAnnotation: "Rückgängig",
  clearAnnotations: "Alles löschen",
  confirmClearAnnotations: "Alle Anmerkungen löschen?",
  textPrompt: "Hinzuzufügender Text:",
};

const fr: Strings = {
  documentTitle: "Visionneuse Géo 3D — Trimble Connect",
  appTitle: "Visionneuse Géo 3D",
  collapsePanel: "Réduire le panneau",
  showPanel: "Afficher le panneau",
  starting: "Démarrage…",
  dropHint: "Déposez ici un GeoTIFF / une archive de mesh / un jeu OBJ",

  view2D: "2D",
  view3D: "3D",
  view2DTitle: "Passer en vue plan 2D (vue du dessus verrouillée, déplacement et zoom uniquement)",
  view3DTitle: "Passer en vue 3D (orbite libre)",

  statusConnecting: "Connexion à Trimble Connect…",
  statusReady: "Prêt",
  statusConnectedTo: (name) => `Connecté à « ${name} »`,
  statusNoProject: "Connecté, mais aucun projet n'est ouvert",
  statusStandalone: "Exécution autonome — ouvrez des fichiers locaux ou une URL",
  statusFilesUnavailable: "Connecté, mais les fichiers du projet sont indisponibles",

  authGateTitle: "Geo 3D Viewer",
  authGateSigningIn: "Vérification de votre compte Trimble Connect…",
  authGateSignInFailed: (error) => `Connexion via Trimble Connect impossible : ${error}`,
  authGateNotWhitelisted: (email) =>
    `Votre compte Trimble (${email}) n'est pas encore autorisé pour cet outil. Demandez à un administrateur de l'ajouter.`,
  authGateError: (error) => `Impossible de joindre le serveur : ${error}`,
  authGateRetry: "Réessayer",
  standaloneBlockedTitle: "Geo 3D Viewer",
  standaloneBlockedMessage: "Cette visionneuse ne fonctionne que comme extension Trimble Connect. Ouvrez-la depuis un projet Trimble Connect.",
  statusResolvingProject: (name) => `Résolution du projet ${name}…`,
  statusResolvingProjectGeneric: "Résolution du projet",
  statusOpening: (label) => `Ouverture de ${label}`,
  statusReading: (label) => `Lecture de ${label}`,
  statusIndexing: (label) => `Indexation de ${label}`,
  statusSaving: (name) => `Enregistrement de « ${name} »`,
  statusUpdatingMeasurements: "Mise à jour des mesures enregistrées",
  statusOpeningShareLink: "Ouverture du lien de partage",
  statusLocatingTiles: (done, total) => `Localisation des tuiles du mesh ${done}/${total}`,
  statusReadingElevations: (done, total) => `Lecture des altitudes ${done}/${total}`,

  toastActionFailed: (label, error) => `Échec — ${label} : ${error}`,
  toastCouldNotReachFiles: (error) => `Impossible d'accéder aux fichiers du projet : ${error}`,
  toastGeographicCrs: (label) =>
    `${label} est en coordonnées géographiques (lon/lat). Cette visionneuse travaille dans ` +
    `un système projeté et ne reprojette pas, elle ne s'alignera donc pas avec le mesh.`,
  toastFolderPairAmbiguous: (name) =>
    `Impossible de distinguer un GeoTIFF DOM (altitude) d'un GeoTIFF Ortho (photo) dans « ${name} ». ` +
    `Ouvrez les fichiers individuellement à la place.`,
  toastCrsMismatch: (name, mismatch) => `${name} : ${mismatch}`,
  toastNoOffset: (label) =>
    `${label} n'a pas de décalage de géoréférencement — placé à l'origine de la scène. ` +
    `Utilisez « Définir le décalage manuellement… » dans le panneau Mesh pour le positionner correctement.`,
  toastTokenAndProjectRequired: "Un jeton d'accès et un ID de projet sont tous deux requis.",
  toastReachedProject: (name, baseUrl) => `${name} atteint via ${baseUrl}`,
  toastNotShareLink: "Cela ne ressemble pas à un lien de partage Trimble Connect.",
  toastShareOpened: (count, permission, baseUrl) =>
    `Partage ouvert : ${count} objet(s), permission ${permission}, via ${baseUrl}`,
  toastShareFolder: "Le partage contient un dossier — parcourez-le ci-dessous pour choisir des fichiers.",
  toastUnsupportedFile: (name) => `${name} : type de fichier non pris en charge par cette visionneuse.`,
  toastCouldNotLoadMeasurements: (error) => `Impossible de charger les mesures enregistrées : ${error}`,
  toastConnectToSaveMeasurements: "Connectez-vous à un projet Trimble Connect pour enregistrer des mesures.",
  toastOpenModelFirstToSave: "Ouvrez d'abord un orthophoto ou un mesh — les mesures sont enregistrées avec le modèle.",
  toastSavedWith: (name, model) => `« ${name} » enregistré avec ${model}.`,
  toastOpenDatasetFirst: "Ouvrez d'abord un jeu de données pour établir le système de coordonnées.",

  data: "Données",
  loaded: "Chargé",
  loadedFolder: (name) => `Chargé — 📦 ${name}`,
  remove: "Retirer",
  filesUnavailableHint: "Les fichiers du projet sont indisponibles — vérifiez que l'extension a reçu l'accès.",
  standaloneHint: "Ne s'exécute pas dans Trimble Connect. Glissez-déposez un fichier sur la visionneuse, ou utilisez un jeton d'accès ci-dessous.",
  useAccessToken: "Utiliser un jeton d'accès…",
  loadingBadge: "chargement",

  terrain: "Terrain",
  tilesBadge: (done, total) => `${done}/${total} tuiles`,
  visible: "Visible",
  opacity: "Opacité",
  wireframe: "Fil de fer",
  backdropOpacity: "Opacité du fond",
  imageryGsd: "GSD imagerie",
  modelGsd: "GSD modèle",
  tileSize: "Taille de tuile",
  textures: "Textures",

  mesh: "Mesh",
  shading: "Ombrage",
  shadingUnlit: "Non éclairé (tel que capturé)",
  shadingLit: "Éclairé (ombrage de relief)",
  offset: "Décalage",
  offsetSource: "Source du décalage",
  triangles: "Triangles",
  resident: "En mémoire",
  located: "Localisées",
  estimatedExtents: "Étendues estimées",
  tilePlacement: "Placement des tuiles",
  tilePlacementExact: "Exact (lit ~12 % de l'archive)",
  tilePlacementFast: "Estimation rapide (~1 %)",
  tilePlacementHint:
    "Les étendues des tuiles déterminent lesquelles se chargent en premier. Le mode exact " +
    "lit le bloc de sommets de chaque tuile ; l'estimation rapide l'échantillonne et peut se " +
    "tromper d'un quart de tuile.",
  setOffsetManually: "Définir le décalage manuellement…",
  offsetPrompt: "Décalage du mesh sous la forme « est nord hauteur » dans le système du projet :",

  view: "Vue",
  verticalExaggeration: "Exagération verticale",
  sunAzimuth: "Azimut du soleil",
  sunElevation: "Élévation du soleil",
  brightness: "Luminosité",
  zoomToExtent: "Zoom sur l'étendue",
  topDown: "Vue du dessus",
  toggleTerrain: "Afficher/masquer le terrain",

  info: "Infos",
  project: "Projet",
  projectCrs: "Système du projet",
  regionApi: "API régionale",
  host: "Hôte",
  hostStandalone: "autonome",
  shareLinks: "Liens de partage",
  shareRegion: "Région du partage",
  orthoCrs: "Système Ortho",
  orthoSize: "Taille Ortho",
  orthoLevels: "Niveaux Ortho",
  domCrs: "Système DOM",
  domSize: "Taille DOM",
  backdrop: "Fond",
  backdropSwisstopo: "swisstopo SWISSIMAGE",
  backdropNone: "aucun",
  sceneOrigin: "Origine de la scène",
  extent: "Étendue",
  elevation: "Altitude",
  infoHint:
    "Les couches sont supposées partager un même système projeté ; aucune reprojection " +
    "n'est effectuée. Les coordonnées sous le curseur s'affichent en bas à droite.",
  eyeDistance: "vue",

  tokenModalTitle: "Utiliser un jeton d'accès Trimble Connect",
  tokenModalHint:
    "Nécessaire uniquement hors de Trimble Connect. Le jeton est le jeton Trimble Identity de " +
    "l'utilisateur connecté ; il est conservé en mémoire pour cette session et jamais stocké.",
  accessToken: "Jeton d'accès",
  projectId: "ID du projet",
  connect: "Connecter",

  loadingFolder: "Chargement…",
  folderListError: (error) => `Impossible de lister le dossier : ${error}`,
  folderEmpty: "Ce dossier est vide.",
  loadFolderPair: (name, count) => `📦 Charger DOM + Ortho depuis « ${name} » (${count} rasters)`,

  measureLine: "Mesurer une ligne",
  measureArea: "Mesurer une surface (plan)",
  measureVolume: "Estimer un volume",
  savedFor: (model) => `Enregistré · ${model}`,
  saved: "Enregistré",
  noSavedForModel: "Aucune mesure enregistrée pour ce modèle pour l'instant.",
  openModelToSave: "Ouvrez un orthophoto ou un mesh pour y enregistrer des mesures.",
  showMeasurement: "Afficher cette mesure",
  renameMeasurement: "Renommer",
  deleteMeasurement: "Supprimer",
  deleteConfirm: (name) => `Supprimer « ${name} » ?`,
  undoPoint: "Annuler le dernier point",
  finish: "Terminer",
  editPoints: "Modifier les points",
  saveMeasurement: "Enregistrer cette mesure",
  clearShape: "Effacer cette forme",
  lengthLabel: "Longueur",
  perimeterLabel: "Périmètre",
  lengthSoFarLabel: "Longueur jusqu'ici",
  areaPlan: "Surface (plan)",
  volumeEst: "Volume (est.)",
  baseElevation: "Altitude de base (m)",
  hintClosed: "Faites glisser un point pour le déplacer, effacez pour remesurer, enregistrez pour la garder, ou choisissez un autre outil pour changer.",
  hintLine: "Cliquez des points sur le modèle ; Terminer verrouille la ligne.",
  hintArea: "Cliquez 3 points ou plus sur le modèle ; Terminer ferme la forme.",
  namePrompt: "Nom de cette mesure :",
  defaultLineName: "Ligne",
  defaultAreaName: "Surface",
  defaultVolumeName: "Volume",

  menuTitle: "Visionneuse Géo 3D",

  modeViewer: "Visionneuse 3D",
  modePlan: "Plan orthophoto",
  modeViewerTitle: "Passer à la visionneuse 3D",
  modePlanTitle: "Passer à l'export de plan orthophoto",
  orthophotos: "Orthophotos",
  noOrthophotosLoaded: "Aucun TIFF chargé — ouvrez-en un depuis le navigateur de projet ou déposez-le ici.",
  addTiffButton: "Ajouter des TIFF…",
  addOrthophotoHint: "Ouvrez un TIFF depuis le navigateur de projet, ou déposez-le sur le canevas. Autant que vous voulez.",
  overlapBadge: "chevauche",
  bringToFront: "Mettre au premier plan",
  layerVisible: "Visible — cliquer pour masquer",
  layerHidden: "Masqué — cliquer pour afficher",
  removeLayer: "Retirer",
  toastPlanCrsMismatch: (name, mismatch) => `${name} : ${mismatch} — l'alignement peut être faux.`,
  printExport: "Impression & export",
  scaleLabel: "Échelle",
  paperSize: "Format papier",
  orientationLabel: "Orientation",
  orientationPortrait: "Portrait",
  orientationLandscape: "Paysage",
  dpiLabel: "Résolution",
  exportPdf: "Exporter en PDF",
  statusExportingPdf: "Génération du PDF…",
  toastExportedPdf: "PDF exporté.",
  toastExportFailed: (error) => `Échec de l'export : ${error}`,
  exportNeedsLayer: "Chargez d'abord au moins une orthophoto.",
  annotate: "Annoter",
  toolPan: "Déplacer",
  toolPen: "Peindre",
  toolText: "Texte",
  penColor: "Couleur",
  penWidth: "Épaisseur",
  undoAnnotation: "Annuler",
  clearAnnotations: "Tout effacer",
  confirmClearAnnotations: "Effacer toutes les annotations ?",
  textPrompt: "Texte à ajouter :",
};

const it: Strings = {
  documentTitle: "Visualizzatore Geo 3D — Trimble Connect",
  appTitle: "Visualizzatore Geo 3D",
  collapsePanel: "Comprimi pannello",
  showPanel: "Mostra pannello",
  starting: "Avvio…",
  dropHint: "Trascina qui un GeoTIFF / un archivio mesh / un set OBJ",

  view2D: "2D",
  view3D: "3D",
  view2DTitle: "Passa alla vista 2D (dall'alto, bloccata: solo pan e zoom)",
  view3DTitle: "Passa alla vista 3D (orbita libera)",

  statusConnecting: "Connessione a Trimble Connect…",
  statusReady: "Pronto",
  statusConnectedTo: (name) => `Connesso a “${name}”`,
  statusNoProject: "Connesso, ma nessun progetto è aperto",
  statusStandalone: "In esecuzione autonoma — apri file locali o un URL",
  statusFilesUnavailable: "Connesso, ma i file del progetto non sono disponibili",

  authGateTitle: "Geo 3D Viewer",
  authGateSigningIn: "Verifica dell'account Trimble Connect…",
  authGateSignInFailed: (error) => `Accesso tramite Trimble Connect non riuscito: ${error}`,
  authGateNotWhitelisted: (email) =>
    `Il tuo account Trimble (${email}) non è ancora abilitato per questo strumento. Chiedi a un amministratore di aggiungerlo.`,
  authGateError: (error) => `Impossibile raggiungere il server: ${error}`,
  authGateRetry: "Riprova",
  standaloneBlockedTitle: "Geo 3D Viewer",
  standaloneBlockedMessage: "Questo visualizzatore funziona solo come estensione di Trimble Connect. Aprilo da un progetto Trimble Connect.",
  statusResolvingProject: (name) => `Risoluzione del progetto ${name}…`,
  statusResolvingProjectGeneric: "Risoluzione del progetto",
  statusOpening: (label) => `Apertura di ${label}`,
  statusReading: (label) => `Lettura di ${label}`,
  statusIndexing: (label) => `Indicizzazione di ${label}`,
  statusSaving: (name) => `Salvataggio di “${name}”`,
  statusUpdatingMeasurements: "Aggiornamento delle misurazioni salvate",
  statusOpeningShareLink: "Apertura del link di condivisione",
  statusLocatingTiles: (done, total) => `Localizzazione tile del mesh ${done}/${total}`,
  statusReadingElevations: (done, total) => `Lettura delle quote ${done}/${total}`,

  toastActionFailed: (label, error) => `${label} non riuscito: ${error}`,
  toastCouldNotReachFiles: (error) => `Impossibile raggiungere i file del progetto: ${error}`,
  toastGeographicCrs: (label) =>
    `${label} è in coordinate geografiche (lon/lat). Questo visualizzatore lavora in un CRS ` +
    `proiettato e non riproietta, quindi non si allineerà con il mesh.`,
  toastFolderPairAmbiguous: (name) =>
    `Impossibile distinguere un GeoTIFF DOM (quote) da un GeoTIFF Ortho (foto) in “${name}”. ` +
    `Apri i file singolarmente.`,
  toastCrsMismatch: (name, mismatch) => `${name}: ${mismatch}`,
  toastNoOffset: (label) =>
    `${label} non ha un offset di georeferenziazione — posizionato all'origine della scena. ` +
    `Usa “Imposta offset manualmente…” nel pannello Mesh per posizionarlo correttamente.`,
  toastTokenAndProjectRequired: "Sono necessari sia un token di accesso sia un ID progetto.",
  toastReachedProject: (name, baseUrl) => `${name} raggiunto via ${baseUrl}`,
  toastNotShareLink: "Non sembra un link di condivisione di Trimble Connect.",
  toastShareOpened: (count, permission, baseUrl) =>
    `Condivisione aperta: ${count} oggetto/i, permesso ${permission}, via ${baseUrl}`,
  toastShareFolder: "La condivisione contiene una cartella — sfogliala qui sotto per scegliere i file.",
  toastUnsupportedFile: (name) => `${name}: tipo di file non supportato da questo visualizzatore.`,
  toastCouldNotLoadMeasurements: (error) => `Impossibile caricare le misurazioni salvate: ${error}`,
  toastConnectToSaveMeasurements: "Connettiti a un progetto Trimble Connect per salvare le misurazioni.",
  toastOpenModelFirstToSave: "Apri prima un ortofoto o un mesh — le misurazioni vengono salvate insieme al modello.",
  toastSavedWith: (name, model) => `“${name}” salvata con ${model}.`,
  toastOpenDatasetFirst: "Apri prima un set di dati per stabilire il sistema di coordinate.",

  data: "Dati",
  loaded: "Caricato",
  loadedFolder: (name) => `Caricato — 📦 ${name}`,
  remove: "Rimuovi",
  filesUnavailableHint: "I file del progetto non sono disponibili — verifica che l'estensione abbia ottenuto l'accesso.",
  standaloneHint: "Non in esecuzione all'interno di Trimble Connect. Trascina un file sul visualizzatore, oppure usa un token di accesso qui sotto.",
  useAccessToken: "Usa un token di accesso…",
  loadingBadge: "caricamento",

  terrain: "Terreno",
  tilesBadge: (done, total) => `${done}/${total} tile`,
  visible: "Visibile",
  opacity: "Opacità",
  wireframe: "Wireframe",
  backdropOpacity: "Opacità dello sfondo",
  imageryGsd: "GSD immagine",
  modelGsd: "GSD modello",
  tileSize: "Dimensione tile",
  textures: "Texture",

  mesh: "Mesh",
  shading: "Ombreggiatura",
  shadingUnlit: "Non illuminato (come acquisito)",
  shadingLit: "Illuminato (ombreggiatura del rilievo)",
  offset: "Offset",
  offsetSource: "Origine dell'offset",
  triangles: "Triangoli",
  resident: "In memoria",
  located: "Localizzate",
  estimatedExtents: "Estensioni stimate",
  tilePlacement: "Posizionamento tile",
  tilePlacementExact: "Esatto (legge ~12% dell'archivio)",
  tilePlacementFast: "Stima rapida (~1%)",
  tilePlacementHint:
    "Le estensioni delle tile determinano quali si caricano per prime. La modalità esatta " +
    "legge il blocco di vertici di ogni tile; la stima rapida lo campiona e può sbagliare di " +
    "un quarto di tile.",
  setOffsetManually: "Imposta offset manualmente…",
  offsetPrompt: "Offset del mesh come “est nord quota” nel CRS del progetto:",

  view: "Vista",
  verticalExaggeration: "Esagerazione verticale",
  sunAzimuth: "Azimut solare",
  sunElevation: "Elevazione solare",
  brightness: "Luminosità",
  zoomToExtent: "Zoom sull'estensione",
  topDown: "Vista dall'alto",
  toggleTerrain: "Mostra/nascondi terreno",

  info: "Info",
  project: "Progetto",
  projectCrs: "CRS del progetto",
  regionApi: "API regionale",
  host: "Host",
  hostStandalone: "autonomo",
  shareLinks: "Link di condivisione",
  shareRegion: "Regione di condivisione",
  orthoCrs: "CRS Ortho",
  orthoSize: "Dimensione Ortho",
  orthoLevels: "Livelli Ortho",
  domCrs: "CRS DOM",
  domSize: "Dimensione DOM",
  backdrop: "Sfondo",
  backdropSwisstopo: "swisstopo SWISSIMAGE",
  backdropNone: "nessuno",
  sceneOrigin: "Origine della scena",
  extent: "Estensione",
  elevation: "Quota",
  infoHint:
    "Si presume che gli strati condividano un unico CRS proiettato; non viene eseguita alcuna " +
    "riproiezione. Le coordinate sotto il cursore sono mostrate in basso a destra.",
  eyeDistance: "vista",

  tokenModalTitle: "Usa un token di accesso Trimble Connect",
  tokenModalHint:
    "Necessario solo fuori da Trimble Connect. Il token è il token Trimble Identity dell'utente " +
    "connesso; viene mantenuto in memoria solo per questa sessione e non viene mai salvato.",
  accessToken: "Token di accesso",
  projectId: "ID progetto",
  connect: "Connetti",

  loadingFolder: "Caricamento…",
  folderListError: (error) => `Impossibile elencare la cartella: ${error}`,
  folderEmpty: "Questa cartella è vuota.",
  loadFolderPair: (name, count) => `📦 Carica DOM + Ortho da “${name}” (${count} raster)`,

  measureLine: "Misura una linea",
  measureArea: "Misura un'area (piano)",
  measureVolume: "Stima un volume",
  savedFor: (model) => `Salvate · ${model}`,
  saved: "Salvate",
  noSavedForModel: "Nessuna misurazione ancora salvata per questo modello.",
  openModelToSave: "Apri un ortofoto o un mesh per salvare misurazioni ad esso associate.",
  showMeasurement: "Mostra questa misurazione",
  renameMeasurement: "Rinomina",
  deleteMeasurement: "Elimina",
  deleteConfirm: (name) => `Eliminare “${name}”?`,
  undoPoint: "Annulla ultimo punto",
  finish: "Termina",
  editPoints: "Modifica punti",
  saveMeasurement: "Salva questa misurazione",
  clearShape: "Cancella questa forma",
  lengthLabel: "Lunghezza",
  perimeterLabel: "Perimetro",
  lengthSoFarLabel: "Lunghezza finora",
  areaPlan: "Area (piano)",
  volumeEst: "Volume (stima)",
  baseElevation: "Quota di base (m)",
  hintClosed: "Trascina un punto per spostarlo, cancella per rimisurare, salva per conservarla, oppure scegli un altro strumento per cambiare.",
  hintLine: "Clicca i punti sul modello; Termina blocca la linea.",
  hintArea: "Clicca 3 o più punti sul modello; Termina chiude la forma.",
  namePrompt: "Nome di questa misurazione:",
  defaultLineName: "Linea",
  defaultAreaName: "Area",
  defaultVolumeName: "Volume",

  menuTitle: "Visualizzatore Geo 3D",

  modeViewer: "Visualizzatore 3D",
  modePlan: "Piano ortofoto",
  modeViewerTitle: "Passa al visualizzatore 3D",
  modePlanTitle: "Passa all'esportazione del piano ortofoto",
  orthophotos: "Ortofoto",
  noOrthophotosLoaded: "Nessun TIFF caricato — aprine uno dal browser di progetto o trascinalo qui.",
  addTiffButton: "Aggiungi file TIFF…",
  addOrthophotoHint: "Apri un TIFF dal browser di progetto, oppure trascinalo sulla tela. Quanti ne vuoi.",
  overlapBadge: "si sovrappone",
  bringToFront: "Porta in primo piano",
  layerVisible: "Visibile — clic per nascondere",
  layerHidden: "Nascosto — clic per mostrare",
  removeLayer: "Rimuovi",
  toastPlanCrsMismatch: (name, mismatch) => `${name}: ${mismatch} — l'allineamento potrebbe non essere corretto.`,
  printExport: "Stampa ed esporta",
  scaleLabel: "Scala",
  paperSize: "Formato carta",
  orientationLabel: "Orientamento",
  orientationPortrait: "Verticale",
  orientationLandscape: "Orizzontale",
  dpiLabel: "Risoluzione",
  exportPdf: "Esporta PDF",
  statusExportingPdf: "Generazione PDF…",
  toastExportedPdf: "PDF esportato.",
  toastExportFailed: (error) => `Esportazione non riuscita: ${error}`,
  exportNeedsLayer: "Carica prima almeno un'ortofoto.",
  annotate: "Annota",
  toolPan: "Sposta",
  toolPen: "Dipingi",
  toolText: "Testo",
  penColor: "Colore",
  penWidth: "Spessore penna",
  undoAnnotation: "Annulla",
  clearAnnotations: "Cancella tutto",
  confirmClearAnnotations: "Cancellare tutte le annotazioni?",
  textPrompt: "Testo da aggiungere:",
};

const DICTS: Record<Lang, Strings> = { en, de, fr, it };

/** Trimble Connect language codes seen in the wild, normalised to our supported set. */
const ALIASES: Record<string, Lang> = {
  en: "en",
  "en-us": "en",
  "en-gb": "en",
  de: "de",
  "de-de": "de",
  "de-ch": "de",
  "de-at": "de",
  fr: "fr",
  "fr-fr": "fr",
  "fr-ch": "fr",
  "fr-ca": "fr",
  "fr-be": "fr",
  it: "it",
  "it-it": "it",
  "it-ch": "it",
};

/** Maps a raw `UserSettings.language` value to a language this viewer has strings for. */
export function detectLanguage(raw: string | null | undefined): Lang {
  if (!raw) return "en";
  const key = raw.trim().toLowerCase();
  if (key in ALIASES) return ALIASES[key];
  const primary = key.split(/[-_]/)[0];
  return (ALIASES[primary] as Lang | undefined) ?? "en";
}

let current: Lang = "en";

export function setLanguage(lang: Lang): void {
  current = lang;
}

export function getLanguage(): Lang {
  return current;
}

/** The active language's strings. Call fresh at each use — never cache the result. */
export function t(): Strings {
  return DICTS[current];
}
